import { useState, useEffect, useRef, useMemo } from 'react'

import { ClientError } from '@tribeplatform/gql-client/lib'
import { Form } from '@tribeplatform/react-components/Form'
import type { FormRef } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  useNetwork,
  useUpdateNetworkMediaSettings,
} from '@tribeplatform/react-sdk/hooks'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { MultiselectCreatable } from '@tribeplatform/react-ui-kit/Multiselect'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { SuggestedFileTypes } from './components/SuggestedFileTypes.js'

export const MediaSettings = () => {
  const { $t } = useI18n()
  const formRef = useRef<FormRef>()
  const { mutateAsync: updateMediaSettings, isLoading } =
    useUpdateNetworkMediaSettings()

  // Fetch limits separately since it's not in the cache
  const { data: network } = useNetwork({
    fields: { limits: 'all', settings: 'basic' },
    ignorePlaceholderData: true,
  })
  const { settings } = network ?? {}

  const [fileTypes, setFileTypes] = useState(settings?.fileTypesLimit || [])

  const hasAddedNewFileTypes =
    fileTypes.filter(type => {
      return settings?.fileTypesLimit.indexOf(type) < 0
    }).length > 0

  const MEGA_BYTE = 1024 * 1024

  const defaultValues = useMemo(
    () => ({ fileSizeLimit: settings?.fileSizeLimit / MEGA_BYTE }),
    [MEGA_BYTE, settings?.fileSizeLimit],
  )

  useEffect(() => {
    if (settings?.fileTypesLimit) {
      setFileTypes(settings.fileTypesLimit)
    }
  }, [settings?.fileTypesLimit])

  useEffect(() => {
    if (defaultValues?.fileSizeLimit) {
      formRef.current.methods.reset(defaultValues)
    }
  }, [defaultValues])

  return (
    <Card>
      <Card.Header
        title={$t({
          defaultMessage: 'Media settings',
          id: 'Admin.MediaSettings',
        })}
      />
      <Card.Content>
        <Form
          defaultValues={defaultValues}
          ref={formRef}
          onSubmit={async data => {
            const { fileSizeLimit } = data
            try {
              await updateMediaSettings({
                input: {
                  fileSizeLimit: fileSizeLimit * MEGA_BYTE,
                  fileTypesLimit: fileTypes,
                },
              })
              toast({
                title: $t({
                  defaultMessage: 'Media settings updated',
                  id: 'Admin.MediaSettings.Updated',
                }),
                status: 'success',
              })
            } catch (error) {
              const message =
                (error as ClientError)?.response?.errors
                  ?.map(e => e.message)
                  ?.join('\n') || error.message

              toast({
                title:
                  message ||
                  $t({
                    defaultMessage: 'Unable to update media settings',
                    id: 'Admin.MediaSettings.FailedToUpdate',
                  }),
                status: 'error',
              })
            }
          }}
        >
          <div className="flex flex-col space-y-5">
            <Form.Input
              label={$t({
                defaultMessage: 'Attachment and video size limit',
                id: 'Admin.MediaSettings.SizeLimits',
              })}
              helperText={$t({
                defaultMessage:
                  'The maximum size for each attachment or inline video.',
                id: 'Admin.MediaSettings.SizeLimits.Description',
              })}
              name="fileSizeLimit"
              type="number"
              min="1"
            />
            <FormControl>
              <FormControl.Label>
                <div className="flex space-s-1">
                  <span>
                    <T
                      defaultMessage="Supported file types"
                      id="Admin.MediaSettings.TypeLimits"
                    />
                  </span>
                </div>
              </FormControl.Label>
              <MultiselectCreatable
                value={fileTypes}
                options={[]}
                onChange={setFileTypes}
                searchable
                onCreateItem={inputValue => {
                  setFileTypes([...fileTypes, inputValue])
                }}
              >
                <MultiselectCreatable.Button
                  placeholder={$t({
                    defaultMessage: 'Add file type...',
                    id: 'Admin.MediaSettings.AddFileType',
                  })}
                />
                <MultiselectCreatable.Items>
                  {({ creating, inputValue }) => {
                    return (
                      <>
                        {creating && inputValue?.length > 0 && (
                          <MultiselectCreatable.Item
                            value={inputValue}
                            index={0}
                          >
                            <span>
                              <T defaultMessage="Add" id="Generics.Add" />
                            </span>{' '}
                            <span className="font-bold">{inputValue}</span>
                          </MultiselectCreatable.Item>
                        )}
                      </>
                    )
                  }}
                </MultiselectCreatable.Items>
              </MultiselectCreatable>
              <SuggestedFileTypes
                fileTypes={fileTypes}
                addFileType={fileType =>
                  setFileTypes(prev => [...prev, fileType])
                }
              />
              <FormControl.Meta
                helperText={
                  <>
                    <T
                      defaultMessage="Define what file types can be uploaded in your community. You can use either <link>MIME types</link> (e.g. image/*) or extensions (e.g. .docx)."
                      id="Admin.MediaSettings.FileTypeMetaHelper"
                      values={{
                        link: chunks => (
                          <Link
                            href="https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {chunks}
                          </Link>
                        ),
                      }}
                    />
                  </>
                }
              />
              {hasAddedNewFileTypes && (
                <div className="pt-2">
                  <Alert
                    status="warning"
                    title={$t({
                      defaultMessage:
                        'Please note that there is a security risk involved by allowing additional file types.',
                      id: 'Admin.MediaSettings.FileTypeRiskAlert',
                    })}
                  >
                    <T
                      defaultMessage="We strongly encourage you to only accept file types that you need for your business. Accepting compressed files can include file types that you haven’t listed."
                      id="Admin.MediaSettings.FileTypeRiskAlert.Description"
                    />
                  </Alert>
                </div>
              )}
            </FormControl>
            <div className="flex justify-end">
              <Button loading={isLoading} type="submit">
                <T defaultMessage="Update" id="Generics.Update" />
              </Button>
            </div>
          </div>
        </Form>
      </Card.Content>
    </Card>
  )
}
